<template>
  <form @submit.prevent="submit">
    <Field
      label="Display Title"
      @input="$v.resource.name.$touch()"
      v-model="resource.name"
      placeholder="e.g. Page banner"
      helpText="This is for internal use only"
      :error="nameError"
    />

    <Field
      hidden
      label="Content Type"
      type="select"
      @input="$v.resource.list_type_id.$touch()"
      v-model="resource.list_type_id"
      :error="list_type_idError"
      :options="listTypes"
    />

    <div
      class="input centered"
      v-if="suggestedTypesLoaded && recommendedListTypes.length">
      <label>Suggested for this page</label>

      <div class="content-types">
        <div
          v-for="(type, index) in recommendedListTypes"
          :key="`type-${index}`"
          :class="['content-type', {'is-active': activeClass(type)}]"
          :style="{background: type.value}"
          @click="setType(type)"
        >
          <div>
            <Sprite :name="`list-${type.key}`" />
            <p>{{ type.key | humanize }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="input centered">
      <label>All Widgets</label>

      <div class="content-types">
        <div
          v-for="(type, index) in listTypes"
          :key="`type-${index}`"
          :class="['content-type', {'is-active': activeClass(type)}]"
          :style="{background: type.value}"
          @click="setType(type)"
        >
          <span class="new-label" v-if="isNew(type)">New</span>

          <div>
            <Sprite :name="`list-${type.key}`" />
            <p>{{ type.key | humanize }}</p>
          </div>
        </div>
      </div>
    </div>

    <Field
      hidden
      label="weight"
      @input="$v.resource.weight.$touch()"
      v-model="resource.weight"
      :error="weightError"
      helpText="This is the order which the content will be on the page"
    />

    <div class="btn-save">
      <Btn
        :isLoading="btnLoading"
        primary>
        Create Page Section
      </Btn>
    </div>
  </form>
</template>

<script>
import form from '@/mixins/form'
import suggestedListTypes from '@/mixins/suggestedListTypes'

import Btn from '@/components/Btn'
import Sprite from '@/components/Sprite'

import {errorMessages} from '@/lib/error_messages'
import {required} from 'vuelidate/lib/validators'

export default {
  name: 'ListForm',

  mixins: [form, suggestedListTypes],

  data() {
    return {
      activeType: '',
      suggestedTypesLoaded: false,
      recommendedListTypes: []
    }
  },

  components: {
    Btn,
    Sprite
  },

  props: {
    listTypes: {
      type: Array,
      required: true
    },

    totalLists: {
      type: Number
    },

    pageName: {
      type: String
    },

    btnLoading: {
      type: Boolean
    }
  },

  computed: {
    ...errorMessages(['name', 'list_type_id', 'weight']),

    activeClass() {
      return (value) => {
        return value.key === this.activeType
      }
    },

    isNew() {
      return (type) => {
        let today = new Date()
        let typeCreatedDate = new Date(type.created_at)
        let newDayCount = 30 // Show 'new' label if < 30 days old

        let diff = Math.abs(today.getTime() - typeCreatedDate.getTime())

        return diff / (1000 * 60 * 60 * 24) < newDayCount
      }
    }
  },

  methods: {
    setType(type) {
      this.resource.list_type_id = type.value
      this.activeType = type.key
      this.$emit('typeChange', type)

      if (!this.resource.name) {
        let weight = this.totalLists + 1
        this.resource.name = `${this.pageName} Content ${weight}`
      }
    },

    setInitialListWeight() {
      this.resource.weight = this.totalLists + 1
    },

    recommendListTypes() {
      let pageName = this.pageName.toLowerCase()
      this.suggestedTypes.forEach((type) => {
        if (type.pageNames.includes(pageName)) {
          type.widgets.forEach((widget) => {
            this.listTypes.map((t) => {
              if (t.key === widget) {
                this.recommendedListTypes.push(t)
              }
            })
          })
        }
      })
    }
  },

  filters: {
    humanize(value) {
      return value.replace(/-/g, ' ')
    }
  },

  validations: {
    resource: {
      name: {required},
      list_type_id: {required},
      weight: {required}
    }
  },

  created() {
    this.setInitialListWeight()
  },

  mounted() {
    this.recommendListTypes()
  },

  watch: {
    listTypes() {
      this.recommendListTypes()
    },

    pageName() {
      this.recommendListTypes()
    }
  }
}
</script>

<style lang="sass">
@import '../../stylesheets/base/variables'
@import '../../stylesheets/base/theme'

.content-types
  display: flex
  flex-wrap: wrap
  justify-content: center
  align-items: center
  text-transform: uppercase
  font-weight: bold
  position: relative

  .new-label
    position: absolute
    top: -18px
    left: -38px
    transform: rotate(-30deg)
    font-size: 9px
    transition: all .2s ease-in-out
    background: rgba(0, 0, 0, 0.05)
    width: 100px
    text-align: center
    padding: 25px 0 10px
    color: rgba(0, 0, 0, 0.2)

  .content-type
    display: flex
    flex-direction: column
    justify-content: center
    text-align: center
    align-items: center
    cursor: pointer
    width: 148px
    height: 148px
    margin: 8px
    border-radius: 5px
    border: 2px solid rgba(0, 0, 0, 0.05)
    transition: all .2s ease-in-out
    overflow: hidden
    position: relative

    p
      font-size: 10px

    &.is-active
      transform: scale(1.1)
      border: 2px solid rgba(0, 0, 0, 0.15)

      .new-label
        color: rgba(0, 0, 0, 0.5)

  .sprite
    width: 30px
    height: 30px

    @include themify($themes)
      fill: themed('color-secondary')
</style>
